/**
 * SETTINGS-02 — the Bounding Boxes settings tab.
 *
 * Per-kind (AI findings / Defects) appearance for the Viewer + Tiles
 * overlays. A segmented control switches which kind the controls edit.
 * Colours use a curated brand-safe swatch palette plus an advanced hex
 * field (Q4 → option b). A self-contained live preview renders a sample
 * box + label from the current values over a split bright/dark backdrop
 * so the analyst can judge contrast + the contrasting-outline without
 * leaving the modal (Q4).
 *
 * Persistence: the lyse-prefs PATCH is top-level last-write-wins on nested
 * objects, so every change sends the WHOLE `boundingBox` block.
 */
import { useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { color } from "@aeriosiq/ui";
import {
  useLysePreferencesQuery,
  useUpdateLysePreferencesMutation,
  type BboxKindStyle,
  type BoundingBoxPreferences,
} from "../../preferences/index.js";

type Kind = "ai" | "defect";

/**
 * The overlay's ground is the photograph on the Analysis canvas — a calibrated
 * near-black in both themes — so every value the user can pick, and every
 * value the preview paints, is read from `color.dark`. This is also what keeps
 * the "currently selected" ring on a swatch working: the factory defaults in
 * `preferences/schema.ts` come from this same set, so a default is always one
 * of the swatches below in BOTH themes. A mode-dependent palette would leave
 * the light-mode picker with nothing highlighted.
 */
const canvas = color.dark;

/**
 * The palette a user picks FROM.
 *
 * It used to be ten Apple HIG hues chosen by hand — a fifth ramp, offered to
 * users, that the design system had never heard of. It is now the system's own
 * encoding set: D84's six `series` marks (already ordered to stay apart from
 * each other and from the ground, and already the source of the bbox
 * defaults), plus the two D83 severity slots that `series` does not contain,
 * plus the two neutral extremes the label-colour field needs. Still ten, and
 * every one of them a token — so a user cannot pick their way outside the
 * system, and the advanced hex field below stays the deliberate escape hatch
 * for when they must.
 */
const SWATCHES: readonly string[] = [
  ...canvas.series, // D84 series 1–6 (1 = accent, 2 = brand violet, …)
  canvas.severity.high, // D83's one value with no `status`/`series` twin
  canvas.severity.info, // = status.neutral — the "inert / ungraded" grey
  canvas.text.primary, // the light extreme, for label text on a filled box
  canvas.neutralDeep, // the dark extreme (--ink-900)
];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 0.5, gap: 2 }}
    >
      <FormLabel sx={{ fontSize: 13, color: "text.primary" }}>
        {label}
      </FormLabel>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {children}
      </Box>
    </Stack>
  );
}

function ColorField({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (hex: string) => void;
  ariaLabel: string;
}) {
  const [hex, setHex] = useState(value);
  // Keep the local hex field in sync when the upstream value changes
  // (e.g. switching kind or reset).
  if (value !== hex && HEX_RE.test(value) && hex === "") setHex(value);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {SWATCHES.map((sw) => (
        <Tooltip key={sw} title={sw}>
          <Box
            role="button"
            aria-label={`${ariaLabel}: ${sw}`}
            onClick={() => {
              onChange(sw);
              setHex(sw);
            }}
            sx={{
              width: 18,
              height: 18,
              borderRadius: "4px",
              backgroundColor: sw,
              cursor: "pointer",
              border:
                value.toLowerCase() === sw.toLowerCase()
                  ? "2px solid"
                  : "1px solid",
              borderColor:
                value.toLowerCase() === sw.toLowerCase()
                  ? "primary.main"
                  : "divider",
              boxSizing: "border-box",
            }}
          />
        </Tooltip>
      ))}
      {/* Advanced free hex (Q4 → b) */}
      <TextField
        size="small"
        value={hex}
        placeholder="#RRGGBB"
        onChange={(e) => {
          const next = e.target.value;
          setHex(next);
          if (HEX_RE.test(next)) onChange(next);
        }}
        slotProps={{
          htmlInput: { "aria-label": `${ariaLabel} hex`, maxLength: 7 },
        }}
        sx={{ width: 96, "& input": { fontFamily: "monospace", fontSize: 12 } }}
      />
    </Box>
  );
}

function PxField({
  value,
  min,
  max,
  onChange,
  ariaLabel,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
  ariaLabel: string;
}) {
  return (
    <TextField
      size="small"
      type="number"
      value={value}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n)));
      }}
      slotProps={{ htmlInput: { min, max, "aria-label": ariaLabel } }}
      sx={{ width: 84 }}
    />
  );
}

function PercentSlider({
  value,
  onChange,
  ariaLabel,
}: {
  value: number; // 0..1
  onChange: (v: number) => void;
  ariaLabel: string;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: 200 }}>
      <Slider
        size="small"
        min={0}
        max={100}
        value={Math.round(value * 100)}
        onChange={(_e, v) => {
          if (typeof v === "number") onChange(v / 100);
        }}
        aria-label={ariaLabel}
      />
      <Typography
        variant="caption"
        sx={{
          width: 36,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {Math.round(value * 100)}%
      </Typography>
    </Box>
  );
}

function SmallSelect({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly { value: string; label: string }[];
  ariaLabel: string;
}) {
  return (
    <TextField
      select
      size="small"
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      slotProps={{ htmlInput: { "aria-label": ariaLabel } }}
      sx={{ minWidth: 150 }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function LivePreview({ k }: { k: BboxKindStyle }) {
  const labelBg =
    k.label.bgColor && k.label.bgColor !== "box" ? k.label.bgColor : k.color;
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        Live preview (left = bright blade · right = dark sky)
      </Typography>
      <Box
        data-testid="bbox-live-preview"
        sx={{
          position: "relative",
          height: 132,
          borderRadius: 1,
          overflow: "hidden",
          // Split backdrop so the analyst can judge the box + outline on
          // both a bright and a dark surface (the two real-world extremes:
          // a sunlit blade and open sky). Those extremes are the two modes'
          // own grounds, so the stops are the light and dark panel tokens
          // rather than a pair of hexes picked to look like them.
          background: `linear-gradient(90deg, ${color.light.surface.subtle} 0 50%, ${canvas.surface.default} 50% 100%)`,
        }}
      >
        {/* The sample box */}
        <Box
          sx={{
            position: "absolute",
            left: "30%",
            top: "28%",
            width: "40%",
            height: "44%",
            border: `${String(k.lineWidth)}px solid ${k.color}`,
            backgroundColor:
              k.fillOpacity > 0
                ? hexToRgba(k.color, k.fillOpacity)
                : "transparent",
            // Mirrors the Viewer's Konva halo, which is `neutralDeep` at the
            // same two alphas — the preview lies if it uses a different dark.
            boxShadow: k.outline
              ? `0 0 0 1px ${hexToRgba(canvas.neutralDeep, 0.85)}, 0 0 2px 1px ${hexToRgba(canvas.neutralDeep, 0.6)}`
              : "none",
          }}
        />
        {/* The label */}
        {k.label.show ? (
          <Box
            sx={{
              position: "absolute",
              left: "30%",
              top: k.label.position === "inside" ? "30%" : "calc(28% - 22px)",
              px: 0.5,
              py: "1px",
              borderRadius: "2px",
              fontSize: `${String(k.label.fontSize)}px`,
              lineHeight: 1.4,
              color: k.label.textColor,
              backgroundColor: hexToRgba(labelBg, k.label.bgOpacity),
              whiteSpace: "nowrap",
            }}
          >
            Sample class
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

// Local hex→rgba (mirrors the Viewer's helper; kept here so the preview
// doesn't depend on the ui package internals).
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m?.[1]) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${String((n >> 16) & 255)}, ${String((n >> 8) & 255)}, ${String(n & 255)}, ${String(alpha)})`;
}

export function BoundingBoxSettingsTab(): React.ReactElement {
  const { prefs } = useLysePreferencesQuery();
  const { update } = useUpdateLysePreferencesMutation();
  const [kind, setKind] = useState<Kind>("ai");

  const bb = prefs.boundingBox;
  const k = bb[kind];

  // Top-level last-write-wins on nested objects → always send the whole
  // boundingBox block with the one kind/field changed.
  function patchKind(partial: Partial<BboxKindStyle>) {
    const next: BoundingBoxPreferences = {
      ...bb,
      [kind]: { ...k, ...partial },
    };
    update({ boundingBox: next });
  }
  function patchLabel(partial: Partial<BboxKindStyle["label"]>) {
    patchKind({ label: { ...k.label, ...partial } });
  }
  function setAutoHide(v: boolean) {
    update({ boundingBox: { ...bb, autoHideLabelsWhenDense: v } });
  }

  return (
    <Box>
      <Typography
        component="h2"
        variant="subtitle2"
        sx={{
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "text.secondary",
          fontWeight: 600,
          mt: 1,
          mb: 1,
        }}
      >
        Bounding boxes
      </Typography>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={kind}
        onChange={(_e, v: Kind | null) => {
          if (v) setKind(v);
        }}
        aria-label="Which bounding-box kind to style"
        sx={{ mb: 1 }}
      >
        <ToggleButton value="ai" aria-label="AI findings">
          AI findings
        </ToggleButton>
        <ToggleButton value="defect" aria-label="Defects">
          Defects
        </ToggleButton>
      </ToggleButtonGroup>

      <LivePreview k={k} />

      <Stack sx={{ mt: 1.5 }} divider={null}>
        <Typography variant="overline" color="text.secondary">
          Box
        </Typography>
        <FieldRow label="Line width (px)">
          <PxField
            value={k.lineWidth}
            min={1}
            max={12}
            onChange={(n) => {
              patchKind({ lineWidth: n });
            }}
            ariaLabel="Line width"
          />
        </FieldRow>
        <FieldRow label="Line width — active (px)">
          <PxField
            value={k.lineWidthActive}
            min={1}
            max={16}
            onChange={(n) => {
              patchKind({ lineWidthActive: n });
            }}
            ariaLabel="Line width active"
          />
        </FieldRow>
        <FieldRow label="Contrasting outline">
          <Checkbox
            size="small"
            checked={k.outline}
            onChange={(e) => {
              patchKind({ outline: e.target.checked });
            }}
            inputProps={{ "aria-label": "Contrasting outline" }}
          />
        </FieldRow>

        <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
          Colour
        </Typography>
        <FieldRow label="Colour">
          <ColorField
            value={k.color}
            onChange={(hex) => {
              patchKind({ color: hex });
            }}
            ariaLabel="Box colour"
          />
        </FieldRow>
        <FieldRow label="Colour — active (selected)">
          <ColorField
            value={k.colorActive}
            onChange={(hex) => {
              patchKind({ colorActive: hex });
            }}
            ariaLabel="Active box colour"
          />
        </FieldRow>
        {kind === "defect" ? (
          <FieldRow label="Colour by">
            <SmallSelect
              value={k.colorBy}
              onChange={(v) => {
                patchKind({ colorBy: v as BboxKindStyle["colorBy"] });
              }}
              ariaLabel="Colour by"
              options={[
                { value: "kind", label: "Single colour" },
                { value: "severity", label: "Severity grade" },
              ]}
            />
          </FieldRow>
        ) : null}

        <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
          Fill
        </Typography>
        <FieldRow label="Fill opacity">
          <PercentSlider
            value={k.fillOpacity}
            onChange={(v) => {
              patchKind({ fillOpacity: v });
            }}
            ariaLabel="Fill opacity"
          />
        </FieldRow>
        <FieldRow label="Fill opacity — active">
          <PercentSlider
            value={k.fillOpacityActive}
            onChange={(v) => {
              patchKind({ fillOpacityActive: v });
            }}
            ariaLabel="Active fill opacity"
          />
        </FieldRow>

        <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
          Label
        </Typography>
        <FieldRow label="Show class name">
          <Checkbox
            size="small"
            checked={k.label.show}
            onChange={(e) => {
              patchLabel({ show: e.target.checked });
            }}
            inputProps={{ "aria-label": "Show class name" }}
          />
        </FieldRow>
        <FieldRow label="Show label when">
          <SmallSelect
            value={k.label.when}
            onChange={(v) => {
              patchLabel({ when: v as BboxKindStyle["label"]["when"] });
            }}
            ariaLabel="Show label when"
            options={[
              { value: "always", label: "Always" },
              { value: "selectedOnly", label: "Selected only" },
            ]}
          />
        </FieldRow>
        <FieldRow label="Font size (px)">
          <PxField
            value={k.label.fontSize}
            min={8}
            max={24}
            onChange={(n) => {
              patchLabel({ fontSize: n });
            }}
            ariaLabel="Label font size"
          />
        </FieldRow>
        <FieldRow label="Text colour">
          <ColorField
            value={k.label.textColor}
            onChange={(hex) => {
              patchLabel({ textColor: hex });
            }}
            ariaLabel="Label text colour"
          />
        </FieldRow>
        <FieldRow label="Background colour">
          <ColorField
            value={k.label.bgColor === "box" ? k.color : k.label.bgColor}
            onChange={(hex) => {
              patchLabel({ bgColor: hex });
            }}
            ariaLabel="Label background colour"
          />
        </FieldRow>
        <FieldRow label="Background opacity">
          <PercentSlider
            value={k.label.bgOpacity}
            onChange={(v) => {
              patchLabel({ bgOpacity: v });
            }}
            ariaLabel="Label background opacity"
          />
        </FieldRow>
        <FieldRow label="Label position">
          <SmallSelect
            value={k.label.position}
            onChange={(v) => {
              patchLabel({ position: v as BboxKindStyle["label"]["position"] });
            }}
            ariaLabel="Label position"
            options={[
              { value: "above", label: "Above box" },
              { value: "inside", label: "Inside box" },
              { value: "auto", label: "Auto" },
            ]}
          />
        </FieldRow>

        <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
          Declutter
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={bb.autoHideLabelsWhenDense}
              onChange={(e) => {
                setAutoHide(e.target.checked);
              }}
              inputProps={{ "aria-label": "Auto-hide labels when dense" }}
            />
          }
          label="Auto-hide labels when many boxes crowd the view (reappear on zoom-in)"
        />
      </Stack>
    </Box>
  );
}
